import { useEffect, useState, useMemo } from 'react';
import { CheckCircle2, AlertTriangle, AlertCircle, Send, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { isSponsorRequired } from '../lib/schema-runtime';

// Visas that require a second party for the readiness check. This list used to
// live here as a local Set; it now comes from isSponsorRequired() so the
// readiness gate, CreateMatter's submit gate and any future blocking gate can
// never disagree about who is mandatory. Same membership as before
// (820/801/309/100/300/600-family/461). The optional 600-tourist Inviter is
// deliberately NOT in it, so a tourist matter with no inviter reads "ready"
// exactly as it does today.

interface ContactSnapshot {
  id: string;
  email: string | null;
  given_names: string | null;
  family_name: string | null;
  activated_at: string | null;
  // Set on first login; "invited" = auth.users exists, not yet logged in.
  user_id: string | null;
}

interface MatterReadinessProps {
  matterId: string;
  visaSubclass: string;
  applicantContactId: string;
  sponsorContactId: string | null;
  // Surface a banner when the matter was created via GHL with missing fields.
  // Pass the array straight from sales_background.missing_fields if present.
  ghlMissingFields?: string[] | null;
  // Reload key when the parent re-fetches matters; bump to force refetch.
  reloadKey?: number;
}

type Readiness =
  | { status: 'loading' }
  | { status: 'cannot-invite'; reasons: string[]; applicant: ContactSnapshot; sponsor: ContactSnapshot | null }
  | { status: 'invites-pending'; pending: ContactSnapshot[]; applicant: ContactSnapshot; sponsor: ContactSnapshot | null }
  | { status: 'active'; applicant: ContactSnapshot; sponsor: ContactSnapshot | null };

/**
 * Top-of-page chip showing whether the matter is ready for client/sponsor
 * activity. Tracks three states:
 *   green  -- both required contacts have logged in (activated_at is set).
 *   amber  -- contacts exist + emails on file, but at least one hasn't logged in.
 *   red    -- a required contact is missing email/name (cannot invite).
 *
 * Red state surfaces an inline Quick-Fix form so staff can fill in the missing
 * details and re-trigger the welcome email without leaving the page.
 *
 * Also surfaces a separate orange warning when a GHL deal-Won webhook created
 * the matter with missing required fields.
 */
export default function MatterReadiness({
  matterId,
  visaSubclass,
  applicantContactId,
  sponsorContactId,
  ghlMissingFields,
  reloadKey = 0,
}: MatterReadinessProps) {
  const sponsorRequired = isSponsorRequired(visaSubclass);
  const [readiness, setReadiness] = useState<Readiness>({ status: 'loading' });
  const [internalReload, setInternalReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ids = [applicantContactId];
      if (sponsorContactId) ids.push(sponsorContactId);
      const { data, error } = await supabase
        .from('contacts')
        // activated_at is added in migration 20260507000001 -- types may be stale.
        .select('id, email, given_names, family_name, activated_at, user_id' as never)
        .in('id', ids);
      if (cancelled) return;
      if (error || !data) {
        setReadiness({ status: 'loading' });
        return;
      }
      const rows = data as unknown as ContactSnapshot[];
      const applicant = rows.find((c) => c.id === applicantContactId) ?? null;
      const sponsor = sponsorContactId ? rows.find((c) => c.id === sponsorContactId) ?? null : null;
      if (!applicant) {
        setReadiness({ status: 'loading' });
        return;
      }

      // Build red-state reasons.
      const cannotInvite: string[] = [];
      if (!applicant.email) cannotInvite.push('Applicant has no email on file.');
      if (!applicant.given_names || !applicant.family_name) cannotInvite.push('Applicant name is incomplete.');
      if (sponsorRequired) {
        if (!sponsor) {
          cannotInvite.push('No sponsor contact attached. Required for this visa.');
        } else {
          if (!sponsor.email) cannotInvite.push('Sponsor has no email on file.');
          if (!sponsor.given_names || !sponsor.family_name) cannotInvite.push('Sponsor name is incomplete.');
        }
      }

      if (cannotInvite.length > 0) {
        setReadiness({ status: 'cannot-invite', reasons: cannotInvite, applicant, sponsor });
        return;
      }

      // Amber: pending sign-ins.
      const pending: ContactSnapshot[] = [];
      if (!applicant.activated_at) pending.push(applicant);
      if (sponsorRequired && sponsor && !sponsor.activated_at) pending.push(sponsor);

      if (pending.length > 0) {
        setReadiness({ status: 'invites-pending', pending, applicant, sponsor });
        return;
      }

      setReadiness({ status: 'active', applicant, sponsor });
    })();
    return () => {
      cancelled = true;
    };
  }, [applicantContactId, sponsorContactId, sponsorRequired, reloadKey, internalReload]);

  return (
    <div className="space-y-3">
      {/* GHL missing-fields banner. Independent of readiness status. */}
      {ghlMissingFields && ghlMissingFields.length > 0 && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900 flex items-start gap-2">
          <AlertCircle size={18} className="text-orange-500 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold mb-0.5">Created from GHL with missing fields</div>
            <div className="text-orange-800">
              The deal-Won webhook ran but didn't include: <strong>{ghlMissingFields.join(', ')}</strong>. Fix this in GHL or use Quick-Fix below to fill the gaps.
            </div>
          </div>
        </div>
      )}

      <ReadinessChip
        readiness={readiness}
        sponsorRequired={sponsorRequired}
        matterId={matterId}
        onFixed={() => setInternalReload((k) => k + 1)}
      />
    </div>
  );
}

function ReadinessChip({
  readiness,
  sponsorRequired,
  matterId,
  onFixed,
}: {
  readiness: Readiness;
  sponsorRequired: boolean;
  matterId: string;
  onFixed: () => void;
}) {
  if (readiness.status === 'loading') {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-500 flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Checking matter readiness...
      </div>
    );
  }

  if (readiness.status === 'active') {
    const sponsorBit = sponsorRequired
      ? ' Both applicant and sponsor have signed in.'
      : ' Applicant has signed in.';
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 flex items-start gap-2">
        <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold">Active</div>
          <div className="text-emerald-800">This matter is ready.{sponsorBit}</div>
        </div>
      </div>
    );
  }

  if (readiness.status === 'invites-pending') {
    const names = readiness.pending.map((c) =>
      [c.given_names, c.family_name].filter(Boolean).join(' ').trim() || c.email || 'unnamed contact',
    );
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2">
        <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-semibold">Invites pending</div>
          <div className="text-amber-800">
            Waiting for sign-in: <strong>{names.join(' + ')}</strong>. They've been emailed but haven't logged in yet.
          </div>
        </div>
      </div>
    );
  }

  // cannot-invite: render Quick Fix.
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900">
      <div className="flex items-start gap-2 mb-3">
        <AlertCircle size={18} className="text-red-600 shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold">Cannot invite yet</div>
          <ul className="list-disc list-inside text-red-800 mt-1 space-y-0.5">
            {readiness.reasons.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
      </div>
      <QuickFixForm
        applicant={readiness.applicant}
        sponsor={readiness.sponsor}
        sponsorRequired={sponsorRequired}
        matterId={matterId}
        onFixed={onFixed}
      />
    </div>
  );
}

function QuickFixForm({
  applicant,
  sponsor,
  sponsorRequired,
  matterId,
  onFixed,
}: {
  applicant: ContactSnapshot;
  sponsor: ContactSnapshot | null;
  sponsorRequired: boolean;
  matterId: string;
  onFixed: () => void;
}) {
  const [applicantGiven, setApplicantGiven] = useState(applicant.given_names ?? '');
  const [applicantFamily, setApplicantFamily] = useState(applicant.family_name ?? '');
  const [applicantEmail, setApplicantEmail] = useState(applicant.email ?? '');
  const [sponsorGiven, setSponsorGiven] = useState(sponsor?.given_names ?? '');
  const [sponsorFamily, setSponsorFamily] = useState(sponsor?.family_name ?? '');
  const [sponsorEmail, setSponsorEmail] = useState(sponsor?.email ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sponsorNeedsCreate = sponsorRequired && !sponsor;
  const showSponsorBlock = sponsorRequired;

  const canSubmit = useMemo(() => {
    if (!applicantGiven.trim() || !applicantFamily.trim() || !applicantEmail.trim()) return false;
    if (sponsorRequired) {
      if (!sponsorGiven.trim() || !sponsorFamily.trim() || !sponsorEmail.trim()) return false;
    }
    return true;
  }, [applicantGiven, applicantFamily, applicantEmail, sponsorRequired, sponsorGiven, sponsorFamily, sponsorEmail]);

  const submit = async () => {
    setError(null);
    setSaving(true);
    try {
      // 1. Patch applicant.
      const applicantPatch: Record<string, string> = {};
      if (applicantGiven.trim() !== (applicant.given_names ?? '')) applicantPatch.given_names = applicantGiven.trim();
      if (applicantFamily.trim() !== (applicant.family_name ?? '')) applicantPatch.family_name = applicantFamily.trim();
      if (applicantEmail.trim().toLowerCase() !== (applicant.email ?? '').toLowerCase()) {
        applicantPatch.email = applicantEmail.trim().toLowerCase();
      }
      if (Object.keys(applicantPatch).length > 0) {
        const { error: e } = await supabase
          .from('contacts')
          .update(applicantPatch as never)
          .eq('id', applicant.id);
        if (e) throw e;
      }

      // 2. Patch or create sponsor.
      let sponsorIdToLink: string | null = sponsor?.id ?? null;
      if (sponsorRequired) {
        if (sponsorNeedsCreate) {
          const trimmed = sponsorEmail.trim().toLowerCase();
          const { data: existing } = await supabase
            .from('contacts')
            .select('id')
            .ilike('email', trimmed)
            .is('deleted_at', null)
            .maybeSingle();
          if (existing?.id) {
            sponsorIdToLink = existing.id;
          } else {
            const { data: created, error: e } = await supabase
              .from('contacts')
              .insert({
                email: trimmed,
                given_names: sponsorGiven.trim() || null,
                family_name: sponsorFamily.trim() || null,
                contact_type: 'sponsor',
              })
              .select('id')
              .single();
            if (e || !created) throw e ?? new Error('Failed to create sponsor');
            sponsorIdToLink = created.id;
          }
          const { error: linkErr } = await supabase
            .from('matters')
            .update({ sponsor_contact_id: sponsorIdToLink })
            .eq('id', matterId);
          if (linkErr) throw linkErr;
        } else if (sponsor) {
          const sponsorPatch: Record<string, string> = {};
          if (sponsorGiven.trim() !== (sponsor.given_names ?? '')) sponsorPatch.given_names = sponsorGiven.trim();
          if (sponsorFamily.trim() !== (sponsor.family_name ?? '')) sponsorPatch.family_name = sponsorFamily.trim();
          if (sponsorEmail.trim().toLowerCase() !== (sponsor.email ?? '').toLowerCase()) {
            sponsorPatch.email = sponsorEmail.trim().toLowerCase();
          }
          if (Object.keys(sponsorPatch).length > 0) {
            const { error: e } = await supabase
              .from('contacts')
              .update(sponsorPatch as never)
              .eq('id', sponsor.id);
            if (e) throw e;
          }
        }
      }

      // 3. Re-trigger welcome emails.
      // We do this by inserting a fresh row into a small queue table (future);
      // for now the simplest path is to issue a no-op UPDATE on matters that
      // re-fires the database webhook. Supabase webhooks fire on UPDATE too,
      // but only the welcome functions handle INSERT. Instead, we delete the
      // existing notifications.welcome rows so the function will re-send when
      // the user clicks "Resend invite" -- and we expose a simple endpoint
      // contract via direct invocation of the edge function.
      //
      // Practical implementation: clear the dedupe rows, then call the edge
      // functions explicitly with a synthesised INSERT-shaped payload. This
      // keeps the welcome logic in one place.
      const portalBase = (import.meta.env.VITE_SUPABASE_URL as string) ?? '';
      const sessionRes = await supabase.auth.getSession();
      const jwt = sessionRes.data.session?.access_token;
      if (portalBase && jwt) {
        // Delete the prior 'sent' notifications so the welcome functions don't
        // dedupe-skip.  (Best-effort; failure here doesn't block.)
        await supabase
          .from('notifications')
          .delete()
          .eq('matter_id', matterId)
          .in('template_key', ['welcome', 'welcome-sponsor']);

        // Synthesise a webhook-style payload and POST to both functions.
        const synthPayload = await buildSynthPayload(matterId);
        if (synthPayload) {
          await Promise.all([
            fetch(`${portalBase}/functions/v1/send-matter-welcome`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${jwt}`,
              },
              body: JSON.stringify(synthPayload),
            }).catch(() => null),
            fetch(`${portalBase}/functions/v1/send-sponsor-welcome`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${jwt}`,
              },
              body: JSON.stringify(synthPayload),
            }).catch(() => null),
          ]);
        }
      }

      onFixed();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Quick-fix failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-md border border-red-300 bg-white p-3 space-y-3">
      <div className="text-xs font-semibold text-red-700">Quick fix</div>
      <FieldRow
        label="Applicant"
        givenNames={applicantGiven}
        onGivenNames={setApplicantGiven}
        familyName={applicantFamily}
        onFamilyName={setApplicantFamily}
        email={applicantEmail}
        onEmail={setApplicantEmail}
      />
      {showSponsorBlock && (
        <FieldRow
          label="Sponsor"
          givenNames={sponsorGiven}
          onGivenNames={setSponsorGiven}
          familyName={sponsorFamily}
          onFamilyName={setSponsorFamily}
          email={sponsorEmail}
          onEmail={setSponsorEmail}
        />
      )}
      {error && <div className="text-xs text-red-700">{error}</div>}
      <div className="flex justify-end">
        <button
          type="button"
          disabled={!canSubmit || saving}
          onClick={submit}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed text-xs"
        >
          {saving ? <><Loader2 size={12} className="animate-spin" /> Saving...</> : <><Send size={12} /> Save & resend invites</>}
        </button>
      </div>
    </div>
  );
}

function FieldRow({
  label,
  givenNames,
  onGivenNames,
  familyName,
  onFamilyName,
  email,
  onEmail,
}: {
  label: string;
  givenNames: string;
  onGivenNames: (v: string) => void;
  familyName: string;
  onFamilyName: (v: string) => void;
  email: string;
  onEmail: (v: string) => void;
}) {
  return (
    <div>
      <div className="text-xs font-medium text-slate-700 mb-1">{label}</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <input
          type="text"
          placeholder="Given names"
          value={givenNames}
          onChange={(e) => onGivenNames(e.target.value)}
          className="form-input text-sm"
        />
        <input
          type="text"
          placeholder="Family name"
          value={familyName}
          onChange={(e) => onFamilyName(e.target.value)}
          className="form-input text-sm"
        />
        <input
          type="email"
          placeholder="email@example.com"
          value={email}
          onChange={(e) => onEmail(e.target.value)}
          className="form-input text-sm"
        />
      </div>
    </div>
  );
}

// Build a synthetic INSERT-shaped payload from the current matter row, so the
// existing welcome functions can run again. Returns null on lookup failure.
async function buildSynthPayload(matterId: string): Promise<unknown | null> {
  const { data, error } = await supabase
    .from('matters')
    .select('id, visa_subclass, variant, primary_contact_id, sponsor_contact_id, assigned_staff_id, status')
    .eq('id', matterId)
    .maybeSingle();
  if (error || !data) return null;
  return {
    type: 'INSERT',
    table: 'matters',
    schema: 'public',
    record: data,
    old_record: null,
  };
}
